import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Archive, ArrowLeft, Mail, Phone, MessageSquare, RefreshCw, Search } from "lucide-react";

const formatReceived = (value) => {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: "Asia/Manila", year: "numeric", month: "long", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  }).format(date);
};

const filters = ["all", "unread", "read", "archived"];
const emailHref = (email, subject) => `mailto:${encodeURIComponent(email).replace(/%40/gi, "@")}?subject=${encodeURIComponent(`Re: ${subject || "Your message to Bible Baptist Church Barani"}`)}`;

export default function VisitorMessagesTab({ role, onUnreadChange }) {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [receivedOn, setReceivedOn] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], total: 0, page_size: 25 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => { setPage(1); }, [status, debouncedSearch, receivedOn]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/visitor-messages", { params: {
        status, search: debouncedSearch, received_on: receivedOn || undefined, page,
      } });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load visitor messages.");
    } finally {
      setLoading(false);
    }
  }, [status, debouncedSearch, receivedOn, page]);

  useEffect(() => { load(); }, [load]);

  const refreshCount = async () => {
    try {
      const { data } = await api.get("/visitor-messages/unread-count");
      onUnreadChange?.(data.unread_count || 0);
    } catch { /* Inbox errors are displayed by load(). */ }
  };

  const openMessage = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/visitor-messages/${id}`);
      let message = data;
      setSelected(message);
      if (data.status === "unread") {
        const updated = await api.patch(`/visitor-messages/${id}`, { status: "read" });
        message = updated.data;
        await Promise.all([load(), refreshCount()]);
      }
      setSelected(message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open the message.");
      load();
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (nextStatus) => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/visitor-messages/${selected.id}`, { status: nextStatus });
      setSelected(data);
      await Promise.all([load(), refreshCount()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update the message.");
    } finally {
      setBusy(false);
    }
  };

  const canArchive = role === "admin" || role === "super_admin";
  const pageCount = Math.max(1, Math.ceil(result.total / (result.page_size || 25)));

  return (
    <section className="rounded-2xl border border-[#E5E0D8] bg-white p-4 sm:p-6" data-testid="visitor-messages-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif-display text-2xl sm:text-3xl text-[#1C201E]">Visitors Messages</h2>
          <p className="text-sm text-[#5C6661]">Messages sent through the public Website form.</p>
        </div>
        <button type="button" onClick={() => { load(); refreshCount(); }} className="inline-flex items-center gap-2 rounded-full border border-[#E5E0D8] px-3 py-2 text-sm hover:bg-[#F3EFEA] focus-visible:ring-2 focus-visible:ring-[#2C4C3B]" aria-label="Refresh visitor messages"><RefreshCw size={16} /> Refresh</button>
      </div>

      {selected ? (
        <article className="max-w-3xl" data-testid="visitor-message-detail">
          <button type="button" onClick={() => setSelected(null)} className="inline-flex items-center gap-2 text-sm text-[#2C4C3B] hover:underline focus-visible:ring-2 focus-visible:ring-[#2C4C3B] mb-5"><ArrowLeft size={16} /> Back to inbox</button>
          <div className="rounded-2xl border border-[#E5E0D8] bg-[#FAF9F6] p-5 sm:p-7">
            <div className="flex flex-wrap justify-between gap-2">
              <h3 className="font-serif-display text-2xl text-[#1C201E] break-words">{selected.name}</h3>
              <span className="text-xs uppercase font-semibold text-[#2C4C3B]">{selected.status}</span>
            </div>
            <p className="mt-1 text-sm text-[#5C6661]">Received {formatReceived(selected.created_at)} (Philippine time)</p>
            {selected.email && <p className="mt-3 text-sm break-all"><span className="font-semibold">Email:</span> <a className="text-[#2C4C3B] underline" href={emailHref(selected.email, selected.subject)}>{selected.email}</a></p>}
            {selected.phone_e164 && <><p className="mt-1 text-sm"><span className="font-semibold">Country code:</span> {selected.country_code}</p><p className="mt-1 text-sm"><span className="font-semibold">Contact number:</span> {selected.phone_number}</p><p className="mt-1 text-sm"><span className="font-semibold">International number:</span> <a className="text-[#2C4C3B] underline" href={`tel:${selected.phone_e164}`}>{selected.phone_e164}</a></p></>}
            {selected.subject && <p className="mt-1 text-sm"><span className="font-semibold">Subject:</span> {selected.subject}</p>}
            <p className="mt-1 text-sm"><span className="font-semibold">Source:</span> {selected.source === "legacy" ? "Older message (source not recorded)" : "Website message form"}</p>
            <div className="mt-6 border-t border-[#E5E0D8] pt-5 whitespace-pre-wrap break-words text-[#1C201E] leading-relaxed">{selected.message || "No message text available."}</div>
            <div className="mt-7 flex flex-wrap gap-2">
              {selected.email && <a className="inline-flex items-center gap-2 rounded-full bg-[#2C4C3B] px-4 py-2 text-sm text-white hover:bg-[#1A3326] focus-visible:ring-2 focus-visible:ring-[#2C4C3B]" href={emailHref(selected.email, selected.subject)}><Mail size={16} /> Email</a>}
              {selected.phone_e164 && <><a href={`tel:${selected.phone_e164}`} className="inline-flex items-center gap-2 rounded-full bg-[#2C4C3B] px-4 py-2 text-sm text-white hover:bg-[#1A3326]"><Phone size={16} /> Call</a><a href={`sms:${selected.phone_e164}`} className="inline-flex items-center gap-2 rounded-full bg-[#2C4C3B] px-4 py-2 text-sm text-white hover:bg-[#1A3326]"><MessageSquare size={16} /> Message</a></>}
              <button type="button" disabled={busy} onClick={() => changeStatus(selected.status === "unread" ? "read" : "unread")} className="rounded-full border border-[#E5E0D8] px-4 py-2 text-sm hover:bg-white disabled:opacity-50">Mark {selected.status === "unread" ? "read" : "unread"}</button>
              {canArchive && <button type="button" disabled={busy} onClick={() => changeStatus(selected.status === "archived" ? "unarchive" : "archived")} className="inline-flex items-center gap-2 rounded-full border border-[#E5E0D8] px-4 py-2 text-sm hover:bg-white disabled:opacity-50"><Archive size={16} /> {selected.status === "archived" ? "Restore" : "Archive"}</button>}
            </div>
          </div>
        </article>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Filter visitor messages">
            {filters.map((option) => <button key={option} type="button" onClick={() => setStatus(option)} aria-pressed={status === option} className={`rounded-full px-4 py-2 text-sm capitalize focus-visible:ring-2 focus-visible:ring-[#2C4C3B] ${status === option ? "bg-[#2C4C3B] text-white" : "bg-[#F3EFEA] text-[#1C201E] hover:bg-[#E5E0D8]"}`}>{option}</button>)}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <label className="relative flex-1"><span className="sr-only">Search visitor messages</span><Search size={17} className="absolute left-3 top-3 text-[#5C6661]" /><input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, or message" className="w-full rounded-xl border border-[#E5E0D8] py-2.5 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]" /></label>
            <label className="text-sm text-[#5C6661]">Received on <input type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} className="block sm:inline-block rounded-xl border border-[#E5E0D8] px-3 py-2 focus:ring-2 focus:ring-[#2C4C3B]" /></label>
          </div>
          {error && <p role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          {loading ? <p className="py-10 text-center text-[#5C6661]">Loading visitor messages…</p> : result.items.length === 0 ? <p className="py-10 text-center text-[#5C6661]">No visitor messages {status === "all" ? "yet" : "for this filter"}.</p> : (
            <div className="divide-y divide-[#E5E0D8]">
              {result.items.map((message) => <button key={message.id} type="button" disabled={busy} onClick={() => openMessage(message.id)} className={`w-full text-left p-4 rounded-lg hover:bg-[#F3EFEA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2C4C3B] ${message.status === "unread" ? "bg-[#F0F6F1]" : ""}`}>
                <div className="flex flex-wrap items-start justify-between gap-2"><span className={`text-[#1C201E] ${message.status === "unread" ? "font-bold" : "font-medium"}`}>{message.name}</span><span className="text-xs text-[#5C6661]">{formatReceived(message.created_at)}</span></div>
                {message.status === "unread" && <span className="mt-1 inline-block rounded-full bg-[#2C4C3B] px-2 py-0.5 text-[10px] font-bold uppercase text-white">New</span>}
                {message.subject && <p className="mt-1 text-sm font-medium text-[#1C201E] break-words">{message.subject}</p>}
                <p className="mt-1 text-sm text-[#1C201E] line-clamp-2 break-words">{message.preview || "No message text available."}</p>
                <p className="mt-2 text-xs text-[#5C6661]">{message.email && message.phone_e164 ? "Email + Phone" : message.email ? "Email" : message.phone_e164 ? "Phone" : "Contact unavailable (older message)"} · <span className="font-semibold text-[#2C4C3B]">View</span></p>
              </button>)}
            </div>
          )}
          {pageCount > 1 && <div className="mt-5 flex items-center justify-between gap-3 text-sm"><button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full border px-4 py-2 disabled:opacity-40">Previous</button><span>Page {page} of {pageCount}</span><button type="button" disabled={page >= pageCount} onClick={() => setPage(page + 1)} className="rounded-full border px-4 py-2 disabled:opacity-40">Next</button></div>}
        </>
      )}
    </section>
  );
}
